import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { CenecLoadingBlock } from './CenecSpinner';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

// Grafana-style panel chrome (mirrors FrequencyDataView for visual cohesion).
const PANEL_BG = '#181b1f';
const PANEL_BG_PLOT = '#111217';
const PANEL_BORDER = 'rgba(255,255,255,0.07)';
const PANEL_GRID = 'rgba(255,255,255,0.08)';
const PANEL_TEXT = '#d8d9da';
const PANEL_TEXT_DIM = '#9da0a4';
const COLOR_ACCENT = '#73BF69';
const COLOR_RED = '#E02F44';

const CATEGORY_LABELS = {
  natural_gas: 'Natural Gas',
  oil: 'Crude Oil',
  ammonia: 'Ammonia',
  ammonium_nitrate: 'Ammonium Nitrate',
  can: 'Calcium Ammonium Nitrate (CAN)',
  urea: 'Urea',
  dap: 'DAP',
  nitrogen_ppi: 'Nitrogen Fertilizer (PPI)',
  nitrogen_eu: 'Nitrogen — EU Average (DG AGRI)',
};

const FREQ_LABEL = {
  minute: 'Live (≈15 min lag)',
  hourly: 'Hourly',
  daily: 'Daily',
  monthly: 'Monthly',
  manual: 'Manual entry',
};

const RANGES = [
  { key: '1d',  label: '1 day'   },
  { key: '7d',  label: '7 days'  },
  { key: '30d', label: '30 days' },
  { key: '1y',  label: '1 year'  },
  { key: 'all', label: 'All'     },
];

function fmtPrice(v, decimals = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtRelativeTime(iso) {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';
  const dt = Date.now() - ts;
  const s = Math.floor(dt / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 60) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

function fmtAbsTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { hour12: false });
}

// =========================================================================
// Main view: list of commodities grouped by category. Clicking a row opens
// the detail panel.
// =========================================================================
export default function CommoditiesView() {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);

  const loadList = useCallback(() => {
    fetch(`${API}/api/commodities/list`)
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((d) => { setList(d.commodities || []); setError(null); })
      .catch((e) => setError(String(e)));
  }, []);

  const loadStats = useCallback(() => {
    fetch(`${API}/api/commodities/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setStats(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadList();
    loadStats();
    const id = setInterval(() => { loadList(); }, 60_000); // refresh table every 60 s
    return () => clearInterval(id);
  }, [loadList, loadStats]);

  const grouped = useMemo(() => {
    if (!list) return [];
    const buckets = new Map();
    for (const c of list) {
      const k = c.category;
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(c);
    }
    return Array.from(buckets.entries()).map(([cat, items]) => ({ cat, items }));
  }, [list]);

  if (selectedId !== null) {
    return (
      <CommodityDetailView
        commodityId={selectedId}
        onBack={() => { setSelectedId(null); loadList(); }}
      />
    );
  }

  return (
    <div style={{ padding: '20px', color: PANEL_TEXT, background: '#0e1014', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: PANEL_TEXT, fontWeight: 600 }}>Commodities</h2>
        <div style={{ fontSize: '12px', color: PANEL_TEXT_DIM }}>
          Auto-refreshes every 60 s · all prices converted to USD via ECB reference rates
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px', background: '#3a1a1f', border: `1px solid ${COLOR_RED}`, borderRadius: '4px', marginBottom: '12px', color: '#ffdcdc' }}>
          Failed to load commodities: {error}
        </div>
      )}

      {!list && !error && <CenecLoadingBlock />}

      {grouped.map(({ cat, items }) => (
        <div key={cat} style={{
          background: PANEL_BG,
          border: `1px solid ${PANEL_BORDER}`,
          borderRadius: '4px',
          marginBottom: '14px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px',
            background: '#22252b',
            borderBottom: `1px solid ${PANEL_BORDER}`,
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            color: PANEL_TEXT,
          }}>
            {CATEGORY_LABELS[cat] || cat}
            <span style={{ marginLeft: '8px', color: PANEL_TEXT_DIM, fontWeight: 400, textTransform: 'none' }}>
              ({items.length})
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#1c1f24', color: PANEL_TEXT_DIM, textAlign: 'left' }}>
                <th style={{ padding: '8px 14px', fontWeight: 500 }}>Name</th>
                <th style={{ padding: '8px 14px', fontWeight: 500 }}>Region</th>
                <th style={{ padding: '8px 14px', fontWeight: 500, textAlign: 'right' }}>Last (native)</th>
                <th style={{ padding: '8px 14px', fontWeight: 500, textAlign: 'right' }}>Last (USD)</th>
                <th style={{ padding: '8px 14px', fontWeight: 500 }}>Unit</th>
                <th style={{ padding: '8px 14px', fontWeight: 500 }}>Updated</th>
                <th style={{ padding: '8px 14px', fontWeight: 500 }}>Source</th>
                <th style={{ padding: '8px 14px', fontWeight: 500 }}>Freq</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const stale = c.last_ts && c.update_freq === 'minute'
                  ? (Date.now() - new Date(c.last_ts).getTime()) > 30 * 60_000
                  : false;
                return (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    style={{
                      cursor: 'pointer',
                      borderTop: `1px solid ${PANEL_BORDER}`,
                      background: 'transparent',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#1f2228'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '10px 14px', color: PANEL_TEXT }}>
                      <div style={{ fontWeight: 500 }}>{c.name}</div>
                      <div style={{ fontSize: '11px', color: PANEL_TEXT_DIM, marginTop: '2px' }}>
                        {c.code}{c.source_details?.purity ? ` · ${c.source_details.purity}` : ''}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', color: PANEL_TEXT_DIM }}>{c.region || '—'}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: PANEL_TEXT }}>
                      {c.last_price !== null
                        ? `${fmtPrice(c.last_price, c.unit === 'index' ? 1 : 2)} ${c.native_currency}`
                        : <span style={{ color: PANEL_TEXT_DIM }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: COLOR_ACCENT, fontWeight: 500 }}>
                      {c.last_price_usd !== null
                        ? fmtPrice(c.last_price_usd, c.unit === 'index' ? 1 : 2)
                        : <span style={{ color: PANEL_TEXT_DIM }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', color: PANEL_TEXT_DIM }}>{c.unit}</td>
                    <td style={{ padding: '10px 14px', color: stale ? COLOR_RED : PANEL_TEXT_DIM }} title={fmtAbsTime(c.last_ts)}>
                      {fmtRelativeTime(c.last_ts)}
                    </td>
                    <td style={{ padding: '10px 14px', color: PANEL_TEXT_DIM, fontSize: '12px' }}>
                      {c.source}{c.source_symbol ? ` · ${c.source_symbol}` : ''}
                    </td>
                    <td style={{ padding: '10px 14px', color: PANEL_TEXT_DIM, fontSize: '12px' }}>
                      {FREQ_LABEL[c.update_freq] || c.update_freq}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {stats && (
        <div style={{ fontSize: '11px', color: PANEL_TEXT_DIM, marginTop: '20px', textAlign: 'right' }}>
          DB: commodity_prices {stats.commodity_prices?.row_count?.toLocaleString()} rows ({stats.commodity_prices?.total_size}) ·
          fx_rates {stats.fx_rates?.row_count?.toLocaleString()} rows ({stats.fx_rates?.total_size})
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Detail view: one commodity, chart, range selector, manual entry form.
// =========================================================================
function CommodityDetailView({ commodityId, onBack }) {
  const [meta, setMeta] = useState(null);
  const [range, setRange] = useState('30d');
  const [series, setSeries] = useState(null);
  const [showUSD, setShowUSD] = useState(true);
  const [error, setError] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPrice, setManualPrice] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualMsg, setManualMsg] = useState(null);

  const loadMeta = useCallback(() => {
    fetch(`${API}/api/commodities/${commodityId}`)
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(setMeta)
      .catch((e) => setError(String(e)));
  }, [commodityId]);

  const loadSeries = useCallback(() => {
    setSeries(null);
    fetch(`${API}/api/commodities/${commodityId}/data?range=${range}`)
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(setSeries)
      .catch((e) => setError(String(e)));
  }, [commodityId, range]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadSeries(); }, [loadSeries]);

  const submitManual = async () => {
    setManualSaving(true);
    setManualMsg(null);
    try {
      const r = await fetch(`${API}/api/commodities/${commodityId}/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: Number(manualPrice), note: manualNote }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed');
      setManualMsg(`Saved: ${j.price} ${j.currency} @ ${fmtAbsTime(j.ts)}`);
      setManualPrice('');
      setManualNote('');
      loadMeta();
      loadSeries();
    } catch (e) {
      setManualMsg(`Error: ${String(e)}`);
    } finally {
      setManualSaving(false);
    }
  };

  const plotData = useMemo(() => {
    if (!series || !series.ts?.length) return null;
    const arr = showUSD && series.fx_rate !== null ? series.price_usd : series.price;
    return [{
      x: series.ts,
      y: arr,
      type: 'scatter',
      mode: 'lines',
      line: { color: COLOR_ACCENT, width: 2 },
      hovertemplate: `%{y:.4f} ${showUSD && series.fx_rate ? 'USD' : series.native_currency}<br>%{x}<extra></extra>`,
    }];
  }, [series, showUSD]);

  const plotLayout = useMemo(() => ({
    paper_bgcolor: PANEL_BG,
    plot_bgcolor: PANEL_BG_PLOT,
    font: { color: PANEL_TEXT, family: 'Inter, "Work Sans", sans-serif' },
    margin: { t: 30, r: 30, b: 50, l: 70 },
    xaxis: {
      gridcolor: PANEL_GRID,
      color: PANEL_TEXT_DIM,
      zeroline: false,
    },
    yaxis: {
      gridcolor: PANEL_GRID,
      color: PANEL_TEXT_DIM,
      title: showUSD && series?.fx_rate
        ? `${meta?.unit?.replace(meta?.native_currency || '', 'USD') || 'USD'}`
        : meta?.unit || '',
      zeroline: false,
    },
    height: 420,
    hovermode: 'x',
  }), [meta, showUSD, series]);

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#ffdcdc' }}>
        <button onClick={onBack} style={btnStyle}>← Back</button>
        <div style={{ marginTop: '16px' }}>Error loading commodity: {error}</div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div style={{ padding: '20px' }}>
        <button onClick={onBack} style={btnStyle}>← Back</button>
        <CenecLoadingBlock />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', color: PANEL_TEXT, background: '#0e1014', minHeight: '100vh' }}>
      <button onClick={onBack} style={btnStyle}>← Back to commodities</button>

      <div style={{ marginTop: '16px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontWeight: 600 }}>{meta.name}</h2>
        <div style={{ color: PANEL_TEXT_DIM, fontSize: '13px', marginTop: '4px' }}>
          {meta.code} · {meta.region || '—'} · unit <strong>{meta.unit}</strong> · native currency <strong>{meta.native_currency}</strong>
          {meta.source_details?.purity && <> · <strong>{meta.source_details.purity}</strong></>}
        </div>
        {meta.notes && (
          <div style={{ color: PANEL_TEXT_DIM, fontSize: '12px', marginTop: '6px', fontStyle: 'italic' }}>
            {meta.notes}
          </div>
        )}
      </div>

      {/* Header stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <StatCard label="Last price (native)"
                  value={meta.last_price !== null ? `${fmtPrice(meta.last_price, meta.unit === 'index' ? 1 : 2)} ${meta.native_currency}` : '—'} />
        <StatCard label="Last price (USD)" highlight
                  value={meta.last_price_usd !== null ? fmtPrice(meta.last_price_usd, meta.unit === 'index' ? 1 : 2) : '—'} />
        <StatCard label="Last update"
                  value={fmtRelativeTime(meta.last_ts)}
                  sub={fmtAbsTime(meta.last_ts)} />
        <StatCard label="History"
                  value={meta.row_count?.toLocaleString() || '0'}
                  sub={meta.first_ts ? `from ${meta.first_ts.slice(0, 10)}` : ''} />
        <StatCard label="FX (→USD)"
                  value={meta.fx_rate !== null ? `${fmtPrice(meta.fx_rate, 4)} ${meta.native_currency}/USD` : (meta.native_currency === 'USD' ? '1.0000 (native USD)' : '— no rate')}
                  sub={meta.fx_ts ? `${meta.fx_source} · ${meta.fx_ts.slice(0, 10)}` : ''} />
        <StatCard label="Source"
                  value={meta.source}
                  sub={meta.source_symbol || ''} />
      </div>

      {/* Range + currency toggles */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        background: PANEL_BG,
        padding: '10px 14px',
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: '4px',
        marginBottom: '12px',
      }}>
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            style={{
              ...btnStyle,
              background: range === r.key ? COLOR_ACCENT : '#22252b',
              color: range === r.key ? '#0a0a0a' : PANEL_TEXT,
              fontWeight: range === r.key ? 600 : 400,
            }}
          >
            {r.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: '12px', color: PANEL_TEXT_DIM, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showUSD}
            onChange={(e) => setShowUSD(e.target.checked)}
            disabled={meta.native_currency === 'USD'}
          />
          Show in USD
        </label>
      </div>

      {/* Chart */}
      <div style={{
        background: PANEL_BG,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: '4px',
        padding: '12px',
        marginBottom: '12px',
      }}>
        {!series ? (
          <CenecLoadingBlock />
        ) : !series.ts?.length ? (
          <div style={{ padding: '40px', textAlign: 'center', color: PANEL_TEXT_DIM }}>
            No data points in this range. {meta.source === 'manual' && 'Use the manual entry form below to add observations.'}
          </div>
        ) : (
          <Plot
            data={plotData}
            layout={plotLayout}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        )}
        {series && series.fx_ts && showUSD && (
          <div style={{ fontSize: '11px', color: PANEL_TEXT_DIM, marginTop: '6px', textAlign: 'right' }}>
            USD conversion uses {series.fx_source} rate {fmtPrice(series.fx_rate, 4)} {series.native_currency}/USD as of {series.fx_ts.slice(0, 10)}
          </div>
        )}
      </div>

      {/* Manual entry (always available, primary for source = manual) */}
      <div style={{
        background: PANEL_BG,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: '4px',
        padding: '12px',
      }}>
        <div
          onClick={() => setManualOpen((x) => !x)}
          style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <span style={{ color: PANEL_TEXT_DIM }}>{manualOpen ? '▾' : '▸'}</span>
          <strong style={{ fontSize: '14px' }}>Manual price entry</strong>
          <span style={{ color: PANEL_TEXT_DIM, fontSize: '12px' }}>
            {meta.source === 'manual'
              ? '— primary entry method for this commodity'
              : '— override / annotate'}
          </span>
        </div>
        {manualOpen && (
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', color: PANEL_TEXT_DIM, gap: '4px' }}>
              Price ({meta.native_currency} per {meta.unit.split('/')[1] || meta.unit})
              <input
                type="number"
                step="0.01"
                value={manualPrice}
                onChange={(e) => setManualPrice(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', color: PANEL_TEXT_DIM, gap: '4px', flex: 1, minWidth: '200px' }}>
              Note (e.g. source bulletin, basis)
              <input
                type="text"
                value={manualNote}
                onChange={(e) => setManualNote(e.target.value)}
                placeholder="Argus 2026-05-23 weekly"
                style={inputStyle}
              />
            </label>
            <button
              onClick={submitManual}
              disabled={manualSaving || !manualPrice}
              style={{ ...btnStyle, background: COLOR_ACCENT, color: '#0a0a0a', fontWeight: 600, padding: '8px 16px' }}
            >
              {manualSaving ? 'Saving…' : 'Save'}
            </button>
            {manualMsg && (
              <div style={{ fontSize: '12px', color: manualMsg.startsWith('Error') ? COLOR_RED : COLOR_ACCENT, marginLeft: '8px' }}>
                {manualMsg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Small UI atoms
// =========================================================================
const btnStyle = {
  padding: '6px 12px',
  background: '#22252b',
  color: PANEL_TEXT,
  border: `1px solid ${PANEL_BORDER}`,
  borderRadius: '3px',
  fontSize: '12px',
  cursor: 'pointer',
};

const inputStyle = {
  padding: '6px 10px',
  background: '#0e1014',
  color: PANEL_TEXT,
  border: `1px solid ${PANEL_BORDER}`,
  borderRadius: '3px',
  fontSize: '13px',
  minWidth: '120px',
};

function StatCard({ label, value, sub, highlight = false }) {
  return (
    <div style={{
      background: PANEL_BG,
      border: `1px solid ${PANEL_BORDER}`,
      borderRadius: '4px',
      padding: '10px 14px',
    }}>
      <div style={{ fontSize: '11px', color: PANEL_TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{
        fontSize: highlight ? '20px' : '15px',
        fontWeight: highlight ? 600 : 500,
        color: highlight ? COLOR_ACCENT : PANEL_TEXT,
        marginTop: '4px',
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: PANEL_TEXT_DIM, marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}
